import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios, formatDateUS } from "@/api";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { BottomNav } from "@/components/shared/Navigation";
import {
  ArrowLeft, CheckCircle, XCircle, AlertTriangle, Loader2, ShieldCheck,
} from "lucide-react";

export default function VerificationsPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [verifications, setVerifications] = useState([]);
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [verifRes, offerRes] = await Promise.all([
          axios.get(`${API}/offers/${businessId}/verifications`),
          axios.get(`${API}/offers/${businessId}`),
        ]);
        setVerifications(verifRes.data);
        setOffer(offerRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [businessId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;

  const confirmed = verifications.filter(v => v.result === "confirmed").length;
  const denied = verifications.filter(v => v.result === "denied").length;
  const changed = verifications.filter(v => v.result === "changed").length;

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="verifications-page">
      <div className="bg-gradient-to-br from-sky-400 to-sky-500 text-white p-4 pt-6">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition" data-testid="verifications-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold flex-1">Community Verifications</h1>
        </div>
        {offer && (
          <div className="flex items-center gap-3">
            <BrandLogo name={offer.name} logoUrl={offer.logo_url} size="md" />
            <div>
              <p className="font-semibold">{offer.name}</p>
              <p className="text-white/70 text-sm">{offer.category}</p>
            </div>
          </div>
        )}
      </div>

      <div className="px-4 mt-4 space-y-4">
        {/* Summary stats */}
        <div className="flex gap-3" data-testid="verification-stats">
          <div className="flex-1 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-emerald-600">{confirmed}</p>
            <p className="text-xs text-emerald-500 font-medium">Confirmed</p>
          </div>
          <div className="flex-1 bg-red-50 border border-red-200 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-red-500">{denied}</p>
            <p className="text-xs text-red-400 font-medium">Denied</p>
          </div>
          <div className="flex-1 bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-amber-500">{changed}</p>
            <p className="text-xs text-amber-400 font-medium">Changed</p>
          </div>
        </div>

        {/* Verification entries */}
        {verifications.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center" data-testid="no-verifications">
            <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No verifications yet</p>
            <p className="text-sm text-slate-400 mt-1">Be the first to verify this deal!</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="verification-list">
            {verifications.map((v, i) => {
              const isConfirmed = v.result === "confirmed";
              const isDenied = v.result === "denied";
              const borderColor = isConfirmed ? "border-emerald-200" : isDenied ? "border-red-200" : "border-amber-200";
              const iconBg = isConfirmed ? "bg-emerald-100" : isDenied ? "bg-red-100" : "bg-amber-100";
              const Icon = isConfirmed ? CheckCircle : isDenied ? XCircle : AlertTriangle;
              const iconColor = isConfirmed ? "text-emerald-500" : isDenied ? "text-red-500" : "text-amber-500";
              const resultLabel = isConfirmed ? "Confirmed" : isDenied ? "Denied" : "Changed";
              const resultColor = isConfirmed ? "text-emerald-600" : isDenied ? "text-red-600" : "text-amber-600";

              return (
                <div key={i} className={`bg-white rounded-xl border ${borderColor} p-4`} data-testid={`verification-entry-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-4.5 h-4.5 ${iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-800">{v.user_name || "Anonymous"}</p>
                        <span className={`text-xs font-bold ${resultColor}`}>{resultLabel}</span>
                      </div>
                      {v.notes && <p className="text-sm text-slate-600 mt-1">{v.notes}</p>}
                      {v.denial_reason && <p className="text-xs text-red-500 mt-1">Reason: {v.denial_reason}</p>}
                      <p className="text-xs text-slate-400 mt-1.5">{formatDateUS(v.created_at)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
